import { LoaderFunctionArgs, redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { auth0Client } from 'src/api/auth0_client';
import { AuthenticationService } from 'src/api/authentication_service';
import { AuthenticationError } from 'src/types/errors';
import { RootLayoutLoaderData } from 'src/types/loaders';
import { logout } from 'src/utils/auth_utils';

/**
 * Root layout loader: fetches user info from the AuthenticationService.
 * On AuthenticationError, logs out via Auth0 and redirects to /login.
 * User is then available to AuthProvider and the rest of the app via the loader data.
 */
export async function rootLayoutLoader(): Promise<RootLayoutLoaderData> {
  try {
    const user = AuthenticationService.getInstance().getUserInfo();
    return { user };
  } catch (error) {
    if (error instanceof AuthenticationError) {
      await logout();
      throw redirect('/login');
    }

    Sentry.captureException(error);
    throw error;
  }
}

/**
 * Login loader: if already authenticated, redirect to app; otherwise call
 * the Auth0 client's loginWithRedirect (with invitation/org params from the URL).
 * The browser navigates to Auth0; we only return if redirect fails.
 */
export async function loginLoader({
  request,
}: LoaderFunctionArgs): Promise<void> {
  const isAuthenticated = await auth0Client.isAuthenticated();

  if (isAuthenticated) {
    throw redirect('/');
  }

  const url = new URL(request.url);
  const authorizationParams: Record<string, string> = {};
  const invitation = url.searchParams.get('invitation');
  const invitationUrl = url.searchParams.get('invitation_url');
  const organizationId = url.searchParams.get('organization');
  const organizationName = url.searchParams.get('organization_name');

  if (invitation) authorizationParams.invitation = invitation;
  if (invitationUrl) authorizationParams.invitation_url = invitationUrl;
  if (organizationId) authorizationParams.organization = organizationId;
  else if (organizationName)
    authorizationParams.organization_name = organizationName;

  await auth0Client.loginWithRedirect({ authorizationParams });
}

export type CallbackLoaderError = {
  success: false;
  error: 'invitation_not_found' | 'unknown';
  message: string;
};

export type CallbackLoaderData = CallbackLoaderError;

/**
 * Callback loader: process Auth0 redirect, exchange code for tokens, load user.
 *
 * On success this loader always redirects by throwing redirect('/'), and therefore
 * never returns a value. The only normal return values are error objects used by
 * the page to show a message and redirect to login.
 */
export async function callbackLoader({
  request,
}: LoaderFunctionArgs): Promise<CallbackLoaderData> {
  const isAuthenticated = await auth0Client.isAuthenticated();

  if (isAuthenticated) {
    throw redirect('/');
  }

  const url = new URL(request.url);
  const errorDescription = url.searchParams.get('error_description');

  try {
    await auth0Client.handleRedirectCallback(url.href);
    throw redirect('/');
  } catch (err) {
    if (err instanceof Response && err.status === 302) {
      throw err;
    }

    const isInvitationError =
      errorDescription === 'invitation not found or already used';

    if (isInvitationError) {
      return {
        success: false,
        error: 'invitation_not_found',
        message:
          'Invitation not found or already used. Redirecting to login...',
      };
    }

    Sentry.captureException(err);
    return {
      success: false,
      error: 'unknown',
      message: 'Something went wrong. Redirecting to login...',
    };
  }
}
